import { useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  HardDrive,
  Pause,
  Play,
  RotateCcw,
  Shield,
  Smartphone,
  X,
} from "lucide-react";
import type { ReleaseDetailResponse, ReleaseSummary } from "../contract.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { copyText } from "../lib/clipboard.js";
import { actorName, formatBytes, formatDateTime } from "../lib/format.js";
import {
  useCopyReleaseMutation,
  useGetReleaseQuery,
  useHaltReleaseMutation,
  useListAppsQuery,
  useReleaseTargetsQuery,
  useRollbackReleaseMutation,
  useSetRolloutMutation,
} from "../store/api.js";
import { CompatibilityVerdict, FingerprintTable } from "./CompatibilityPanel.js";
import { Button } from "@/components/ui/button";

interface ReleaseDrawerProps {
  releaseId: string;
  onClose: () => void;
}

export function ReleaseDrawer({ releaseId, onClose }: ReleaseDrawerProps) {
  const { data, isLoading, refetch } = useGetReleaseQuery(releaseId);
  const [copiedSha, setCopiedSha] = useState(false);
  const [copiedFingerprint, setCopiedFingerprint] = useState(false);
  const [haltRelease, { isLoading: isHalting }] = useHaltReleaseMutation();
  const [setRollout, { isLoading: isUpdatingRollout }] = useSetRolloutMutation();
  const [rollbackRelease, { isLoading: isRollingBack }] = useRollbackReleaseMutation();

  const release = data?.release;
  const version = release?.nativeVersion ? `v${release.nativeVersion}` : "—";
  const rolloutPercent = release?.rolloutPercent ?? 100;
  const sha = release?.sha256 ?? "";
  const fingerprint = release?.fingerprint ?? "";

  function handleCopySha() {
    if (!sha) return;
    void copyText(sha);
    setCopiedSha(true);
    setTimeout(() => setCopiedSha(false), 2000);
  }

  function handleCopyFingerprint() {
    if (!fingerprint) return;
    void copyText(fingerprint);
    setCopiedFingerprint(true);
    setTimeout(() => setCopiedFingerprint(false), 2000);
  }

  async function handleRollback() {
    if (!release) return;
    if (
      !confirm(
        `Apakah Anda yakin ingin melakukan rollback untuk rilis v${release.nativeVersion} (#${release.releaseSeq})?`
      )
    ) {
      return;
    }
    try {
      await rollbackRelease({ id: release.id, reason: "Manual rollback from drawer" }).unwrap();
      alert("Rollback berhasil dipicu. Rilis sehat sebelumnya telah dipublikasikan.");
      onClose();
    } catch (err: unknown) {
      const msg = err && typeof err === "object" && "data" in err ? JSON.stringify(err.data) : String(err);
      alert(`Gagal rollback: ${msg}`);
    }
  }

  const stages = [10, 25, 50, 85, 100];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-xs transition-opacity">
      <div className="flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl overflow-y-auto border-l border-[#EAECF0]">
        {/* Top Header / Breadcrumb */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#EAECF0] bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1.5 text-xs font-medium text-[#667085] hover:text-[#101828] transition-colors"
            >
              <ArrowLeft className="size-4" />
              Kembali ke semua rilis
            </button>
            <span className="text-[#D0D5DD]">/</span>
            <span className="font-mono font-bold text-sm text-[#101828]">{version}</span>
          </div>

          <div className="flex items-center gap-2">
            {release?.status === "active" && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs text-[#344054] border-[#D0D5DD]"
                onClick={() => {
                  void haltRelease({ id: release.id, reason: "Manual pause from drawer" });
                }}
                disabled={isHalting}
              >
                <Pause className="size-3.5" />
                Jeda rollout
              </Button>
            )}

            {release?.status === "active" && rolloutPercent < 100 && (
              <Button
                size="sm"
                className="h-8 gap-1.5 bg-[#0F1B3D] text-white text-xs hover:bg-[#1D2939]"
                onClick={() => {
                  void setRollout({ id: release.id, rolloutPercent: Math.min(100, rolloutPercent + 15) });
                }}
                disabled={isUpdatingRollout}
              >
                Naikkan rollout ⌵
              </Button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-[#98A2B3] hover:text-[#101828]"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        {/* Release Title & Status Badges */}
        <div className="border-b border-[#EAECF0] px-6 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-[#101828] font-mono">{version}</h1>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                release?.status === "active"
                  ? "bg-[#ECFDF3] border border-[#ABEFC6] text-[#027A48]"
                  : release?.status === "halted"
                  ? "bg-[#FEF3F2] border border-[#FECDCA] text-[#B42318]"
                  : "bg-[#F6F7F9] border border-[#EAECF0] text-[#667085]"
              }`}
            >
              <span
                className={`size-1.5 rounded-full ${
                  release?.status === "active"
                    ? "bg-[#17B26A]"
                    : release?.status === "halted"
                    ? "bg-[#F04438]"
                    : "bg-[#98A2B3]"
                }`}
              />
              {release?.status === "active"
                ? rolloutPercent === 100
                  ? "Selesai"
                  : "Aktif"
                : release?.status === "halted"
                ? "Dijeda"
                : "Draf"}
            </span>

            {release?.channel && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#EFF4FF] border border-[#BFDBFE] px-2.5 py-0.5 text-xs font-medium text-[#2563EB] capitalize">
                {release.channel}
              </span>
            )}

            {release?.platform && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#F5F3FF] border border-[#DDD6FE] px-2.5 py-0.5 text-xs font-mono font-medium text-[#6D28D9] uppercase">
                <Smartphone className="size-3" />
                {release.platform}
              </span>
            )}

            {release?.releaseSeq !== undefined && (
              <span className="inline-flex items-center rounded-full bg-[#F6F7F9] border border-[#EAECF0] px-2.5 py-0.5 text-xs font-mono text-[#475467]">
                seq #{release.releaseSeq}
              </span>
            )}

            {release?.mandatory && (
              <span className="inline-flex items-center rounded-full bg-[#FEF3F2] border border-[#FECDCA] px-2.5 py-0.5 text-xs font-medium text-[#B42318]">
                Mandatory
              </span>
            )}
          </div>
        </div>

        {/* Content Body: Two Columns */}
        <div className="p-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main 2-column info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Rollout Adoption Card */}
            <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-[#101828]">Status Rollout Bertahap</h3>
                  <p className="text-xs text-[#667085]">
                    Alokasi probabilitas pembaruan untuk perangkat yang terhubung
                  </p>
                </div>
                <span className="text-xl font-bold font-mono text-[#101828]">{rolloutPercent}%</span>
              </div>

              {/* Progress bar */}
              <div className="mt-3 h-2.5 w-full rounded-full bg-[#EAECF0] overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#17B26A] transition-all"
                  style={{ width: `${rolloutPercent}%` }}
                />
              </div>

              {/* Stages visual step indicator */}
              <div className="mt-5 border-t border-[#EAECF0] pt-4">
                <span className="text-xs font-semibold text-[#344054]">Tahapan Rollout</span>
                <div className="mt-3 flex items-center justify-between">
                  {stages.map((stage) => {
                    const isCompleted = rolloutPercent >= stage;
                    return (
                      <div key={stage} className="flex flex-col items-center">
                        <div
                          className={`size-7 rounded-full flex items-center justify-center text-xs font-mono font-bold ${
                            isCompleted
                              ? "bg-[#17B26A] text-white"
                              : "bg-[#F6F7F9] border border-[#EAECF0] text-[#667085]"
                          }`}
                        >
                          {isCompleted ? <Check className="size-3.5" /> : `${stage}%`}
                        </div>
                        <span className="mt-1 text-[10px] text-[#667085]">{stage}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Real Telemetry Breakdown from Server */}
            <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
              <h3 className="text-sm font-bold text-[#101828]">Telemetri Instalasi Nyata</h3>
              <p className="text-xs text-[#667085]">
                Data langsung dari laporan event perangkat yang ditargetkan rilis ini
              </p>

              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 text-center text-xs">
                <div className="rounded-lg bg-[#F6F7F9] p-3 border border-[#EAECF0]">
                  <span className="text-[#667085] block text-[11px]">Berhasil Diinstal</span>
                  <span className="mt-1 font-mono text-lg font-bold text-[#027A48]">
                    {data?.events.installed.toLocaleString("id-ID") ?? "0"}
                  </span>
                </div>
                <div className="rounded-lg bg-[#F6F7F9] p-3 border border-[#EAECF0]">
                  <span className="text-[#667085] block text-[11px]">Berhasil Diunduh</span>
                  <span className="mt-1 font-mono text-lg font-bold text-[#2563EB]">
                    {data?.events.downloaded.toLocaleString("id-ID") ?? "0"}
                  </span>
                </div>
                <div className="rounded-lg bg-[#F6F7F9] p-3 border border-[#EAECF0]">
                  <span className="text-[#667085] block text-[11px]">Gagal Instal</span>
                  <span className="mt-1 font-mono text-lg font-bold text-[#B42318]">
                    {data?.events.failed.toLocaleString("id-ID") ?? "0"}
                  </span>
                </div>
                <div className="rounded-lg bg-[#F6F7F9] p-3 border border-[#EAECF0]">
                  <span className="text-[#667085] block text-[11px]">Rollback Tercatat</span>
                  <span className="mt-1 font-mono text-lg font-bold text-[#B54708]">
                    {data?.events.rolled_back.toLocaleString("id-ID") ?? "0"}
                  </span>
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-[#F6F7F9] p-3 border border-[#EAECF0] flex items-center justify-between text-xs">
                <span className="text-[#344054] font-medium">Perangkat Aktif Menjalankan Rilis Ini</span>
                <span className="font-mono font-bold text-sm text-[#101828]">
                  {data?.devices.toLocaleString("id-ID") ?? "0"} perangkat
                </span>
              </div>
            </div>

            {/* Real Compatibility Panel from Database */}
            {data?.compatibility && (
              <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs space-y-3">
                <h3 className="text-sm font-bold text-[#101828]">Status Kompatibilitas Perangkat</h3>
                <p className="text-xs text-[#667085]">
                  Pemeriksaan kecocokan fingerprint native binary antara bundle dan perangkat
                </p>
                <CompatibilityVerdict compatibility={data.compatibility} />
                <FingerprintTable rows={data.compatibility.fingerprints} />
              </div>
            )}
          </div>

          {/* Right sidebar with Metadata and Danger Zone */}
          <div className="space-y-6">
            {/* Metadata Card */}
            <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs space-y-4">
              <h3 className="text-sm font-bold text-[#101828]">Informasi Rilis</h3>

              <div className="space-y-3 text-xs">
                <div>
                  <span className="text-[#667085] block">Kanal Distribusi</span>
                  <span className="font-medium text-[#101828] mt-0.5 block">{release?.channel || "—"}</span>
                </div>
                <div>
                  <span className="text-[#667085] block">Platform Target</span>
                  <span className="font-medium text-[#101828] mt-0.5 block uppercase">{release?.platform || "—"}</span>
                </div>
                <div>
                  <span className="text-[#667085] block">Ukuran Berkas Bundle</span>
                  <span className="font-mono font-medium text-[#101828] mt-0.5 block">
                    {release?.sizeBytes ? formatBytes(release.sizeBytes) : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-[#667085] block">Waktu Dibuat</span>
                  <span className="font-mono text-[#101828] mt-0.5 block">
                    {release?.createdAt ? formatDateTime(release.createdAt) : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-[#667085] block">Dipublikasikan Oleh</span>
                  <span className="font-medium text-[#101828] mt-0.5 block">
                    {actorName(release?.createdBy)}
                  </span>
                </div>
                <div>
                  <span className="text-[#667085] block">Fingerprint Native</span>
                  <div className="mt-1 flex items-start justify-between gap-2 rounded bg-[#F6F7F9] p-2 border border-[#EAECF0]">
                    <span className="font-mono text-[11px] text-[#475467] break-all select-all leading-relaxed">
                      {fingerprint || "—"}
                    </span>
                    {fingerprint && (
                      <button
                        type="button"
                        onClick={handleCopyFingerprint}
                        className="text-[#2563EB] hover:text-[#1D4ED8] shrink-0"
                        title="Salin Fingerprint"
                      >
                        {copiedFingerprint ? <Check className="size-3.5 text-[#17B26A]" /> : <Copy className="size-3.5" />}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-[#667085] block">Checksum SHA-256</span>
                  <div className="mt-1 flex items-start justify-between gap-2 rounded bg-[#F6F7F9] p-2 border border-[#EAECF0]">
                    <span className="font-mono text-[11px] text-[#475467] break-all select-all leading-relaxed">
                      {sha || "—"}
                    </span>
                    {sha && (
                      <button
                        type="button"
                        onClick={handleCopySha}
                        className="text-[#2563EB] hover:text-[#1D4ED8] shrink-0"
                        title="Salin Checksum"
                      >
                        {copiedSha ? <Check className="size-3.5 text-[#17B26A]" /> : <Copy className="size-3.5" />}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Catatan Rilis */}
            <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs">
              <h3 className="text-sm font-bold text-[#101828]">Catatan Rilis</h3>
              <p className="mt-2 text-xs text-[#475467] leading-relaxed whitespace-pre-wrap break-words">
                {release?.releaseNotes || "Tidak ada catatan rilis."}
              </p>
            </div>

            {/* Copy this bundle to another native target */}
            {release ? <CopyReleasePanel release={release} /> : null}

            {/* Danger Zone */}
            <div className="rounded-xl border border-[#FECDCA] bg-[#FEF3F2]/50 p-5 shadow-xs space-y-3">
              <div className="flex items-center gap-2 text-[#B42318]">
                <AlertTriangle className="size-4" />
                <h3 className="text-sm font-bold">Zona Berbahaya</h3>
              </div>
              <p className="text-xs text-[#667085]">
                Tindakan rollback akan segera menarik rilis ini dan mengembalikan perangkat ke versi sebelumnya.
              </p>
              <Button
                variant="destructive"
                size="sm"
                className="w-full text-xs gap-1.5"
                onClick={() => void handleRollback()}
                disabled={isRollingBack}
              >
                <RotateCcw className="size-3.5" />
                {isRollingBack ? "Memproses rollback…" : "Rollback ke Versi Sebelumnya"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Republishes this bundle against another native build without uploading the
 * bytes again: the staging-to-production path, and the only place a target is
 * chosen by name instead of by hash.
 */
function CopyReleasePanel({ release }: { release: ReleaseSummary }) {
  const [channel, setChannel] = useState(release.channel);
  const [fingerprint, setFingerprint] = useState("");
  const [activate, setActivate] = useState(false);
  const [message, setMessage] = useState("");

  const apps = useListAppsQuery();
  const channels =
    apps.data?.find((app) => app.slug === release.app)?.channels.map((one) => one.name) ?? [];
  const targets = useReleaseTargetsQuery({
    app: release.app,
    channel,
    platform: release.platform,
  });
  const [copyRelease, { isLoading }] = useCopyReleaseMutation();

  const options = (targets.data?.targets ?? []).filter(
    (one) => !(channel === release.channel && one.fingerprint === release.fingerprint),
  );

  async function handleCopy() {
    if (!fingerprint) return;
    const result = await copyRelease({
      id: release.id,
      channel,
      fingerprint,
      nativeVersion:
        options.find((one) => one.fingerprint === fingerprint)?.nativeVersion || undefined,
      activate,
    });
    setMessage(
      "error" in result
        ? apiErrorMessage(result.error)
        : `Rilis seq ${result.data.release.releaseSeq} dibuat di ${channel} (${
            result.data.release.status === "active" ? "aktif" : "draft"
          }).`,
    );
    if (!("error" in result)) setFingerprint("");
  }

  return (
    <div className="rounded-xl border border-[#EAECF0] bg-white p-5 shadow-xs space-y-3">
      <h3 className="text-sm font-bold text-[#101828]">Salin ke Target Lain</h3>
      <p className="text-xs text-[#667085]">
        Menerbitkan ulang bundle yang sama untuk build native lain. Berkasnya dipakai ulang di
        server, jadi tidak ada unggahan kedua.
      </p>

      <label className="block space-y-1">
        <span className="text-[11px] font-semibold text-[#344054]">Kanal tujuan</span>
        <select
          value={channel}
          onChange={(event) => {
            setChannel(event.target.value);
            setFingerprint("");
          }}
          className="w-full rounded-lg border border-[#D0D5DD] px-2.5 py-1.5 text-xs"
        >
          {channels.map((one) => (
            <option key={one} value={one}>
              {one}
            </option>
          ))}
        </select>
      </label>

      <label className="block space-y-1">
        <span className="text-[11px] font-semibold text-[#344054]">Target native</span>
        <select
          value={fingerprint}
          onChange={(event) => setFingerprint(event.target.value)}
          className="w-full rounded-lg border border-[#D0D5DD] px-2.5 py-1.5 text-xs"
        >
          <option value="">Pilih build tujuan…</option>
          {options.map((one) => (
            <option key={one.fingerprint} value={one.fingerprint}>
              {one.label} · {one.devices} perangkat{one.isTarget ? " · target aktif" : ""}
            </option>
          ))}
        </select>
      </label>

      {options.length === 0 && !targets.isLoading ? (
        <p className="text-[11px] text-[#98A2B3]">
          Kanal ini belum punya build native lain yang dikenal server.
        </p>
      ) : null}

      <label className="flex items-center gap-2 text-xs text-[#344054]">
        <input
          type="checkbox"
          checked={activate}
          onChange={(event) => setActivate(event.target.checked)}
        />
        Langsung aktifkan salinannya
      </label>

      <Button
        size="sm"
        className="w-full text-xs gap-1.5"
        onClick={() => void handleCopy()}
        disabled={!fingerprint || isLoading}
      >
        <Copy className="size-3.5" />
        {isLoading ? "Menyalin…" : "Salin Rilis"}
      </Button>

      {message ? <p className="text-[11px] text-[#475467]">{message}</p> : null}
    </div>
  );
}
